// src/pages/admin/AdminLogsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import {
    Typography, Paper, CircularProgress, Box, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip, TextField,
    Select, MenuItem, FormControl, InputLabel, Button
} from '@mui/material';

const ITEMS_PER_PAGE = 20;

function AdminLogsPage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busca, setBusca] = useState('');
    const [filtroAcao, setFiltroAcao] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const q = query(collection(db, "logs_atividade"), orderBy("data", "desc"));
                const querySnapshot = await getDocs(q);
                setLogs(querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    data: doc.data().data.toDate()
                })));
            } catch (error) {
                console.error("Erro ao buscar logs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, []);

    const filteredLogs = useMemo(() => {
        const termo = busca.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return logs.filter(log => {
            const acaoOk = !filtroAcao || log.acao === filtroAcao;
            if (!busca.trim()) return acaoOk;
            const autor   = (log.autorEmail || '').toLowerCase();
            const alvo    = (log.alvoNome   || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const detalhe = (log.detalhes   || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const buscaOk = autor.includes(termo) || alvo.includes(termo) || detalhe.includes(termo);
            return acaoOk && buscaOk;
        });
    }, [logs, busca, filtroAcao]);

    const totalPages = Math.max(1, Math.ceil(filteredLogs.length / ITEMS_PER_PAGE));
    const paginatedLogs = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return filteredLogs.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredLogs, page]);

    const handleBuscaChange = (e) => { setBusca(e.target.value);      setPage(1); };
    const handleAcaoChange  = (e) => { setFiltroAcao(e.target.value); setPage(1); };
    const handleLimpar = () => { setBusca(''); setFiltroAcao(''); setPage(1); };

    const getActionColor = (action) => {
        switch (action) {
            case 'CRIACAO':     return 'success';
            case 'EXCLUSAO':    return 'error';
            case 'ATUALIZACAO': return 'primary';
            default:            return 'default';
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
    }

    return (
        <>
            <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    label="Buscar por autor, alvo ou detalhe..."
                    sx={{ minWidth: 300 }}
                    value={busca}
                    onChange={handleBuscaChange}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Tipo de Ação</InputLabel>
                    <Select value={filtroAcao} label="Tipo de Ação" onChange={handleAcaoChange}>
                        <MenuItem value=""><em>Todas</em></MenuItem>
                        <MenuItem value="CRIACAO">Criação</MenuItem>
                        <MenuItem value="ATUALIZACAO">Atualização</MenuItem>
                        <MenuItem value="EXCLUSAO">Exclusão</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={handleLimpar}>Limpar</Button>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    {filteredLogs.length} registro{filteredLogs.length !== 1 ? 's' : ''} encontrado{filteredLogs.length !== 1 ? 's' : ''}
                </Typography>
            </Paper>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="tabela de logs">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Data / Horário</strong></TableCell>
                            <TableCell><strong>Ação</strong></TableCell>
                            <TableCell><strong>Autor (Quem fez)</strong></TableCell>
                            <TableCell><strong>Alvo (Afetado)</strong></TableCell>
                            <TableCell><strong>Detalhes</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedLogs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Nenhum registro encontrado.</TableCell>
                            </TableRow>
                        ) : (
                            paginatedLogs.map((log) => (
                                <TableRow
                                    key={log.id}
                                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: '#f5f5f5' } }}
                                >
                                    <TableCell>{log.data.toLocaleString('pt-BR')}</TableCell>
                                    <TableCell>
                                        <Chip label={log.acao} color={getActionColor(log.acao)} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>{log.autorEmail || 'Sistema'}</TableCell>
                                    <TableCell>{log.alvoNome}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>{log.detalhes}</Typography>
                                        {log.alteracoes && log.alteracoes.length > 0 && (
                                            <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.85rem', color: 'text.secondary' }}>
                                                {log.alteracoes.map((alt, i) => <li key={i}>{alt}</li>)}
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredLogs.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setPage(p => p - 1)} disabled={page === 1}>← Anterior</Button>
                    <Typography variant="body2" color="text.secondary">
                        Página {page} de {totalPages} · {filteredLogs.length} registros
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>Próxima →</Button>
                </Box>
            )}
        </>
    );
}

export default AdminLogsPage;
